import React, { useState } from 'react';
import {
  Container, Paper, Typography, Box, Button, Accordion, AccordionSummary, AccordionDetails,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, CircularProgress
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ShareIcon from '@mui/icons-material/Share';
import { useParams, useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import SignatureCanvas from 'react-signature-canvas';

import theme from '../../../theme/theme';
import { DRAFT_KEYS } from '../../../utils/draftUtils';
import DraftSaveButton from '../../../components/DraftSaveButton';

import { useSurgicalSafetyChecklist } from './hooks/useSurgicalSafetyChecklist';
import PatientInformation from './screens/PatientInformation';
import BeforeInduction from './screens/BeforeInduction';
import BeforeSkinIncision from './screens/BeforeSkinIncision';
import BeforePatientLeaves from './screens/BeforePatientLeaves';

const SurgicalSafetyChecklist = () => {
    const { uhid_no } = useParams();
    const navigate = useNavigate();

    const {
        formData, setFormData,
        errors, setErrors,
        success,
        submitStatus,
        handleChange, handleCheckboxChange, handleBlur,
        signaturePadRef, signatureDialogOpen, handleSignatureOpen, handleSignatureClear, handleSignatureClose, handleSignatureSave,
        handleSubmit, handleShareClick,
        mutationLoading
    } = useSurgicalSafetyChecklist(uhid_no, navigate);

    const handleBack = () => {
        navigate(`/mainPage/${uhid_no}`, {
            state: { from: 'PreOperativePhase', status: 'success', step: 1 },
        });
    };

    const handleHomeNavigation = () => {
        navigate('/home');
    };

    const goToVideoRecorder = (formName, uhid) => {
        navigate(`/video?form=${encodeURIComponent(formName)}&uhid_no=${encodeURIComponent(uhid)}`);
    };

    return (
        <ThemeProvider theme={theme}>
            <Container maxWidth="md" sx={{ marginTop: 4, marginBottom: 4 }}>
                <Paper elevation={3} sx={{ padding: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 2, mb: 2 }}>
                        <Typography
                            variant="h4"
                            sx={{
                                color: '#008080',
                                display: 'flex',
                                alignItems: 'center',
                                fontWeight: 600,
                            }}
                        >
                            SURGICAL SAFETY CHECKLIST
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <img src="/logo.png" alt="Logo" className="w-8 h-8" />
                            <Typography
                                variant="h6"
                                sx={{
                                    color: '#008080',
                                    marginLeft: 1,
                                    fontWeight: 500,
                                }}
                            >
                                OTMS
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                        <Button
                            variant="outlined"
                            onClick={handleBack}
                            sx={{ marginLeft: 2 }}
                        >
                            Back
                        </Button>

                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Button
                                variant="contained"
                                onClick={handleHomeNavigation}
                                sx={{ marginRight: 1 }}
                            >
                                Home
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => goToVideoRecorder("SURGICAL SAFETY CHECKLIST", uhid_no)}
                                sx={{ marginRight: 1 }}
                            >
                                Add Iterative Video
                            </Button>
                            <IconButton color="primary" aria-label="share" onClick={handleShareClick}>
                                <ShareIcon />
                            </IconButton>
                        </Box>
                    </Box>

                    <form onSubmit={handleSubmit}>
                        <Typography variant="h6" sx={{ color: '#008080', mb: 2, px: 2 }}>
                            Patient Information
                        </Typography>
                        <Box sx={{ px: 2, mb: 3 }}>
                            <PatientInformation 
                                formData={formData} 
                                setFormData={setFormData} 
                                errors={errors} 
                                setErrors={setErrors}
                                handleChange={handleChange} 
                                handleBlur={handleBlur} 
                            />
                        </Box>

                        <Accordion sx={{ mb: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">Before Induction (Sign In)</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <BeforeInduction 
                                    formData={formData}
                                    setFormData={setFormData}
                                    errors={errors}
                                    setErrors={setErrors}
                                    handleChange={handleChange}
                                    handleCheckboxChange={handleCheckboxChange}
                                    handleBlur={handleBlur}
                                    handleSignatureOpen={handleSignatureOpen}
                                />
                            </AccordionDetails>
                        </Accordion>

                        <Accordion sx={{ mb: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">Before Skin Incision (Time Out)</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <BeforeSkinIncision 
                                    formData={formData}
                                    setFormData={setFormData}
                                    errors={errors}
                                    setErrors={setErrors}
                                    handleChange={handleChange}
                                    handleCheckboxChange={handleCheckboxChange}
                                    handleBlur={handleBlur}
                                    handleSignatureOpen={handleSignatureOpen}
                                />
                            </AccordionDetails>
                        </Accordion>

                        <Accordion sx={{ mb: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">Before Patient Leaves OR (Sign Out)</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <BeforePatientLeaves 
                                    formData={formData}
                                    setFormData={setFormData}
                                    errors={errors}
                                    setErrors={setErrors}
                                    handleChange={handleChange}
                                    handleCheckboxChange={handleCheckboxChange}
                                    handleBlur={handleBlur}
                                    handleSignatureOpen={handleSignatureOpen}
                                />
                            </AccordionDetails>
                        </Accordion>

                        <Box sx={{ display: 'flex', gap: 2, marginTop: 4 }}>
                            <DraftSaveButton
                                formKey={DRAFT_KEYS.SURGICAL_SAFETY_CHECKLIST}
                                uhidNo={uhid_no}
                                formData={formData}
                                onDraftLoaded={(draftData) => setFormData(draftData)}
                                variant="outlined"
                                size="large"
                                isUpdate={success === "success"}
                            />
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                fullWidth
                                size="large"
                                disabled={mutationLoading}
                                startIcon={mutationLoading ? <CircularProgress size={20} color="inherit" /> : null}
                            >
                                {mutationLoading ? 'Submitting...' : (success === "success" ? 'Update Checklist' : 'Submit Checklist')}
                            </Button>
                        </Box>
                    </form>

                    {/* Signature Dialog */}
                    <Dialog open={signatureDialogOpen} onClose={handleSignatureClose} maxWidth="sm" fullWidth>
                        <DialogTitle>Provide Your Signature</DialogTitle>
                        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Box sx={{ border: '1px solid #ccc', borderRadius: 1, mt: 1 }}>
                                <SignatureCanvas 
                                    ref={signaturePadRef} 
                                    canvasProps={{ width: 450, height: 200, className: 'sigCanvas' }} 
                                />
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleSignatureClear} color="error">Clear</Button>
                            <Button onClick={handleSignatureClose}>Cancel</Button>
                            <Button onClick={handleSignatureSave} variant="contained" color="primary">Done</Button>
                        </DialogActions>
                    </Dialog>

                    {/* Feedback SnackBar */}
                    <Snackbar 
                        open={submitStatus.open} 
                        autoHideDuration={6000} 
                        onClose={() => {/* setSubmitStatus logic if needed */}}
                    >
                        <Alert severity={submitStatus.severity} sx={{ width: '100%' }}>
                            {submitStatus.message}
                        </Alert>
                    </Snackbar>
                    
                    {success === "success" && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            You are viewing/updating an existing record for this patient.
                        </Alert>
                    )}
                </Paper>
            </Container>
        </ThemeProvider>
    );
};

export default SurgicalSafetyChecklist;
